import { useEffect, useState, useCallback, useRef } from 'react'

import { useLazyQuery } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { Combobox, TextInput, TextInputProps, useCombobox } from '@mantine/core'
import { useDebouncedState, useInViewport } from '@mantine/hooks'

import { GET_CARRIER_BY_DOT_NUMBER } from 'src/graphql/carriers.graphql'
import { SEARCH_FMCSA_COMPANY_BY_NAME } from 'src/graphql/fmcsa.graphql'

import CarrierList from './CarrierList'
import ContactList from './ContactList'

export interface CarrierAutocompleteProps
  extends Omit<TextInputProps, 'onChange'> {
  onSelect?: (selectedItem) => void
  onContactSelect?: (contact) => void
  enableContacts?: boolean
  classNames?: Record<string, string>
  onChange?: (value: string) => void
  // Where this autocomplete is rendered (e.g. 'driver_application' | 'dashboard'),
  // used to segment fmcsa_* RUM events by surface
  surface?: string
  // Wider than the input when set, opening leftward from its right edge, for
  // narrow inputs whose results need room (DOT number, fleet size, HQ)
  dropdownWidth?: number
}

const CarrierAutocomplete = ({
  onSelect,
  onContactSelect,
  enableContacts = false,
  classNames,
  onChange,
  dropdownWidth,
  surface = 'unknown',
  // Destructured so a consumer's handlers (e.g. Mantine form.getInputProps)
  // compose with the internal dropdown/abandonment handling instead of
  // replacing it via the props spread below
  onFocus,
  onBlur,
  ...props
}: CarrierAutocompleteProps) => {
  const [inputDebounced, setInputDebounced] = useDebouncedState('', 400)
  const [results, setResults] = useState([])
  const [selectedCarrier, setSelectedCarrier] = useState(null)
  const [_selectedContact, setSelectedContact] = useState(null)
  const [showContacts, setShowContacts] = useState(false)
  const { ref, inViewport } = useInViewport()
  const MINIMUM_CHARACTERS = 2

  // Abandonment tracking: refs survive blur/unmount without re-renders.
  // An interaction is "armed" only when the user actually types (so prefilled
  // controlled values never count) and disarmed once the event fires, so it
  // emits at most once per typing interaction.
  const selectedThisInteractionRef = useRef(false)
  const abandonmentArmedRef = useRef(false)
  const currentInputRef = useRef('')
  const resultsRef = useRef([])

  useEffect(() => {
    resultsRef.current = results
  }, [results])

  const maybeTrackAbandonment = useCallback(() => {
    if (!abandonmentArmedRef.current || selectedThisInteractionRef.current) {
      return
    }
    const searchTerm = currentInputRef.current
    if (searchTerm.trim().length < MINIMUM_CHARACTERS) {
      return
    }
    abandonmentArmedRef.current = false
    const resultsCount = resultsRef.current.length

    // Track searches the user walked away from without selecting a carrier
    datadogRum.addAction('fmcsa_search_abandoned', {
      component: 'CarrierAutocomplete',
      search_term: searchTerm,
      results_count: resultsCount,
      had_results: resultsCount > 0,
      search_type: /^\d+$/.test(searchTerm.trim())
        ? 'dot_number'
        : 'company_name',
      surface,
    })
  }, [surface])

  // Deferred so a racing option click (option mousedown/submit can interleave
  // with input blur) marks the selection before abandonment is evaluated
  const scheduleAbandonmentCheck = useCallback(() => {
    window.setTimeout(maybeTrackAbandonment, 200)
  }, [maybeTrackAbandonment])

  const maybeTrackAbandonmentRef = useRef(maybeTrackAbandonment)
  useEffect(() => {
    maybeTrackAbandonmentRef.current = maybeTrackAbandonment
  }, [maybeTrackAbandonment])

  // Treat unmount mid-interaction (e.g. drawer closed, page navigation) as abandonment
  useEffect(() => {
    return () => maybeTrackAbandonmentRef.current()
  }, [])

  const combobox = useCombobox({
    onDropdownClose: () => scheduleAbandonmentCheck(),
  })

  const [
    searchQuery,
    { loading: searching, data: searchResults, error: searchError },
  ] = useLazyQuery(SEARCH_FMCSA_COMPANY_BY_NAME, {
    onCompleted: (data) => {
      const newCompanies = data?.searchResults?.companies
      if (data.searchResults.pageInfo.offset === 0) {
        setResults(newCompanies)

        // Track search results for monitoring
        datadogRum.addAction('fmcsa_search_results', {
          component: 'CarrierAutocomplete',
          search_term: inputDebounced,
          results_count: newCompanies?.length || 0,
          search_type: /^\d+$/.test(inputDebounced.trim())
            ? 'dot_number'
            : 'company_name',
          has_results: (newCompanies?.length || 0) > 0,
          surface,
        })
      } else {
        setResults((state) => [...state, ...newCompanies])
      }
    },
    onError: (error) => {
      // Track search errors
      datadogRum.addAction('fmcsa_search_error', {
        component: 'CarrierAutocomplete',
        search_term: inputDebounced,
        error_message: error.message,
        surface,
      })
    },
  })

  const [
    getCarrierByDotNumber,
    { loading: loadingCarrier, data: carrierData, error: carrierError },
  ] = useLazyQuery(GET_CARRIER_BY_DOT_NUMBER)

  // Separate query just for DB presence tracking
  const [checkCarrierInDb] = useLazyQuery(GET_CARRIER_BY_DOT_NUMBER, {
    onCompleted: (data) => {
      // Track if selected carrier exists in our DB
      if (selectedCarrier) {
        datadogRum.addAction('fmcsa_carrier_db_presence', {
          component: 'CarrierAutocomplete',
          selected_dot_number: selectedCarrier.dotNumber,
          selected_company_name: selectedCarrier.companyName,
          exists_in_db: Boolean(data?.carrierByDotNumber),
          has_contacts: Boolean(data?.carrierByDotNumber?.contacts?.length),
          surface,
        })
      }
    },
  })

  const handleOptionSubmit = (option) => {
    // Mark before any blur/dropdown-close handler evaluates abandonment, so a
    // selection racing an input blur is never miscounted as abandoned
    selectedThisInteractionRef.current = true

    if (showContacts) {
      // Handle contact selection
      setSelectedContact(option)
      // Update input to show contact name with carrier info
      const contactDisplayValue = `${
        option.contactName || 'Unnamed Contact'
      } (${selectedCarrier?.companyName || ''})`
      if (onChange) {
        onChange(contactDisplayValue)
      }
      if (onContactSelect) {
        onContactSelect(option)
      }

      // Track contact selection
      datadogRum.addAction('fmcsa_contact_selected', {
        component: 'CarrierAutocomplete',
        search_term: inputDebounced,
        carrier_dot_number: selectedCarrier?.dotNumber,
        carrier_name: selectedCarrier?.companyName,
        contact_name: option.contactName || 'Unnamed Contact',
        surface,
      })

      combobox.closeDropdown()
    } else {
      // Handle carrier selection
      setSelectedCarrier(option)
      setSelectedContact(null)
      // Update the input value to show the selected carrier
      const carrierName = option.companyName || ''
      if (onChange) {
        onChange(carrierName)
      }

      // 1-based position of the picked option within the displayed results,
      // to measure server-side ranking quality
      const rankIndex = results.findIndex(
        (item) =>
          item === option ||
          (item.dotNumber === option.dotNumber &&
            item.companyName === option.companyName)
      )

      // Track carrier selection (will check DB presence when contacts are loaded)
      datadogRum.addAction('fmcsa_carrier_selected', {
        component: 'CarrierAutocomplete',
        search_term: inputDebounced,
        selected_dot_number: option.dotNumber,
        selected_company_name: option.companyName,
        selected_rank: rankIndex >= 0 ? rankIndex + 1 : null,
        results_count: results.length,
        search_type: /^\d+$/.test(inputDebounced.trim())
          ? 'dot_number'
          : 'company_name',
        will_show_contacts: enableContacts && option.dotNumber,
        surface,
      })

      // Always check if carrier exists in DB for tracking
      if (option.dotNumber) {
        checkCarrierInDb({ variables: { dotNumber: option.dotNumber } })
      }

      if (enableContacts && option.dotNumber) {
        getCarrierByDotNumber({ variables: { dotNumber: option.dotNumber } })
        setShowContacts(true)
        // Don't close dropdown when selecting carrier - keep it open to show contacts
      } else {
        // Close dropdown when contacts are disabled or no dotNumber
        combobox.closeDropdown()
      }
      if (onSelect) {
        onSelect(option)
      }
    }
  }

  const handleBackToCarriers = () => {
    setShowContacts(false)
    setSelectedContact(null)
  }

  // Sync inputDebounced with external value prop when component is controlled
  useEffect(() => {
    if (props.value !== undefined) {
      setInputDebounced(String(props.value))
      currentInputRef.current = String(props.value)
    }
  }, [props.value, setInputDebounced])

  useEffect(() => {
    const trimmed = inputDebounced.trim()
    if (trimmed.length >= MINIMUM_CHARACTERS) {
      searchQuery({ variables: { name: inputDebounced } })
    }
    setResults([])
  }, [inputDebounced, searchQuery])

  // Track manual entry when user types without selecting from results
  useEffect(() => {
    if (
      props.value &&
      String(props.value).length >= MINIMUM_CHARACTERS &&
      !selectedCarrier &&
      !_selectedContact &&
      results.length === 0 &&
      !searching &&
      inputDebounced === String(props.value)
    ) {
      // User has typed a value but no results were found and no selection was made
      datadogRum.addAction('fmcsa_manual_entry', {
        component: 'CarrierAutocomplete',
        manual_input: props.value,
        search_attempted: inputDebounced.length >= MINIMUM_CHARACTERS,
        search_type: /^\d+$/.test(inputDebounced.trim())
          ? 'dot_number'
          : 'company_name',
        surface,
      })
    }
  }, [
    props.value,
    selectedCarrier,
    _selectedContact,
    results.length,
    searching,
    inputDebounced,
    surface,
  ])

  const handleLoadMore = useCallback(() => {
    const { pageInfo } = searchResults?.searchResults || {}
    if (pageInfo && Number.isInteger(pageInfo.offset)) {
      searchQuery({
        variables: {
          name: inputDebounced,
          pageInfo: { offset: pageInfo.offset + 1, limit: pageInfo.limit },
        },
      })
    }
  }, [searchQuery, inputDebounced, searchResults?.searchResults])

  return (
    <Combobox
      onOptionSubmit={handleOptionSubmit}
      store={combobox}
      withinPortal={false}
      position={dropdownWidth ? 'bottom-end' : 'bottom-start'}
      width={dropdownWidth ?? 'target'}
      offset={5}
    >
      <Combobox.Target>
        <TextInput
          classNames={
            classNames || {
              root: 'flex-1',
              label: 'text-xs font-medium text-doubleNickel-gray-700',
              input: `cursor-pointer [&[data-disabled]]:bg-doubleNickel-white [&[data-disabled]]:opacity-100 [&[data-disabled]]:text-doubleNickel-gray-600`,
            }
          }
          onChange={(event) => {
            const newValue = event.currentTarget.value
            if (onChange) {
              onChange(newValue)
            }
            setInputDebounced(newValue)
            // Reset selections when user starts typing
            setSelectedCarrier(null)
            setSelectedContact(null)
            setShowContacts(false)
            // Typing starts a new abandonment-tracking interaction
            currentInputRef.current = newValue
            selectedThisInteractionRef.current = false
            abandonmentArmedRef.current = true
            combobox.resetSelectedOption()
            combobox.openDropdown()
          }}
          onClick={(event) => {
            combobox.openDropdown()
            event.stopPropagation()
          }}
          onFocus={(event) => {
            combobox.openDropdown()
            if (onFocus) {
              onFocus(event)
            }
          }}
          onBlur={(event) => {
            combobox.closeDropdown()
            scheduleAbandonmentCheck()
            if (onBlur) {
              onBlur(event)
            }
          }}
          {...(props as Omit<TextInputProps, 'onChange'>)}
        />
      </Combobox.Target>

      <Combobox.Dropdown
        styles={{ dropdown: { maxWidth: '100vh' } }}
        translate="no"
      >
        <Combobox.Options mah={300} className="overflow-y-auto">
          {showContacts && enableContacts ? (
            <ContactList
              selectedCarrier={selectedCarrier}
              contacts={carrierData?.carrierByDotNumber?.contacts || []}
              loadingCarrier={loadingCarrier}
              carrierError={carrierError}
              onBackToCarriers={handleBackToCarriers}
            />
          ) : (
            <CarrierList
              results={results}
              inputDebounced={inputDebounced}
              searching={searching}
              searchError={searchError}
              minimumCharacters={MINIMUM_CHARACTERS}
              inViewport={inViewport}
              searchResults={searchResults}
              onLoadMore={handleLoadMore}
              viewportRef={ref}
            />
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export default CarrierAutocomplete
